import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, ListChecks } from 'lucide-react';
import { listLscNeedsAttention } from '../../api';
import { useLeagueReadiness } from '../../hooks/useLeagueReadiness';
import { mapReadinessToSetupSummary } from '../../leagueReadinessMapper';
import { lscPath } from '../../routing';
import LeagueSetupHomeCard from '../LeagueSetupHomeCard';
import LiveReviewNeededBanner from '../leagueHome/LiveReviewNeededBanner';

const ROW =
  'flex min-h-12 flex-wrap items-center gap-3 rounded-stream-md border border-stream-line bg-stream-surface px-4 py-2';

function warningLine(headline: string, warningCount: number, blockerCount: number): string {
  if (warningCount > 0) {
    return `${headline}: ${warningCount} warning${warningCount === 1 ? '' : 's'}`;
  }
  if (blockerCount > 0) {
    return `${headline}: ${blockerCount} blocker${blockerCount === 1 ? '' : 's'}`;
  }
  return headline;
}

export type StreamOperatorStripProps = {
  leagueId: string;
  isCommissioner: boolean;
  isOwner: boolean;
};

const StreamOperatorStrip: React.FC<StreamOperatorStripProps> = ({
  leagueId,
  isCommissioner,
  isOwner,
}) => {
  const { readiness } = useLeagueReadiness(leagueId);
  const summary = useMemo(
    () => (readiness ? mapReadinessToSetupSummary(readiness) : null),
    [readiness]
  );
  const [setupOpen, setSetupOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    if (!isCommissioner) {
      setReviewCount(0);
      return;
    }
    let cancelled = false;
    listLscNeedsAttention(leagueId)
      .then((items) => {
        if (cancelled) return;
        setReviewCount(
          items.filter((item) => item.is_live_exception && item.state !== 'commissioner_approved').length
        );
      })
      .catch(() => {
        if (!cancelled) setReviewCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [isCommissioner, leagueId]);

  const setupText = summary
    ? warningLine(summary.headline, summary.warningCount, summary.blockerCount)
    : 'Finish setup';

  return (
    <div data-testid="stream-operator-strip" className="flex flex-col gap-2">
      {reviewCount > 0 ? (
        <div className={ROW} data-testid="stream-operator-review">
          <AlertTriangle className="h-4 w-4 shrink-0 text-stream-expire" aria-hidden />
          <p className="font-stream-body text-sm text-stream-ink">
            {`Live review: ${reviewCount} game${reviewCount === 1 ? '' : 's'}`}
          </p>
          <Link
            to={lscPath(`/leagues/${leagueId}/needs-attention`)}
            className="inline-flex min-h-12 items-center rounded-stream-md bg-stream-ink px-4 font-stream-display text-sm font-bold uppercase tracking-wide text-stream-ground"
          >
            Review now
          </Link>
          <button
            type="button"
            className="font-stream-body text-sm text-stream-muted"
            data-testid="stream-operator-review-toggle"
            aria-expanded={reviewOpen}
            onClick={() => setReviewOpen((open) => !open)}
          >
            {reviewOpen ? 'Hide details' : 'Show details'}
          </button>
        </div>
      ) : null}
      {reviewOpen ? (
        <LiveReviewNeededBanner leagueId={leagueId} isCommissioner={isCommissioner} />
      ) : null}

      <div className={ROW} data-testid="stream-operator-setup">
        <ListChecks className="h-4 w-4 shrink-0 text-stream-ink" aria-hidden />
        <p className="font-stream-body text-sm text-stream-ink" data-testid="stream-operator-setup-line">
          {setupText}
        </p>
        <Link
          to={lscPath(`/leagues/${leagueId}/setup`)}
          className="inline-flex min-h-12 items-center rounded-stream-md bg-stream-ink px-4 font-stream-display text-sm font-bold uppercase tracking-wide text-stream-ground"
          data-testid="stream-operator-setup-wizard"
        >
          Open setup wizard
        </Link>
        <button
          type="button"
          className="font-stream-body text-sm text-stream-muted"
          data-testid="stream-operator-setup-toggle"
          aria-expanded={setupOpen}
          onClick={() => setSetupOpen((open) => !open)}
        >
          {setupOpen ? 'Hide details' : 'Show details'}
        </button>
      </div>
      {setupOpen ? <LeagueSetupHomeCard leagueId={leagueId} isOwner={isOwner} /> : null}
    </div>
  );
};

export default StreamOperatorStrip;
